import { faTrashCan } from '@fortawesome/pro-light-svg-icons'
import { useCallback, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router'
import PermissionChecker from '../../../../../../../../../../../baseComponents/PermissionChecker'
import { TwinIconToolTip } from '../../../../../../../../../../../baseComponents/TwinIcon'
import { OrderData, SetCompleteDataTy } from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/types'
import { getBasePath } from '../../../../../../../../../../../utils/globals/link'
import useEditDeleteModal from '../../../../../../../../../../../utils/hooks/useEditDeleteModal'
import useTwinTranslation from '../../../../../../../../../../../utils/hooks/useTwinTranslation'
import { getUserInfo, getUserPermissions } from '../../../../../../../../../../../utils/reducers/getters'
import { SellDownloadPdfTemplateButton, SellDownloadButtonPDF, SellPdfPreview, SellViewWorkSheetButton, SellDownloadButtonCSV, SellQuickAccessButton, SellDownloadTags } from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderSellLayout/SellButtons'
import { checkSellFromOrderAllOk } from '../../../../../../../../../../../utils/globals/order/sell'
import { SellInvoiceDuplicateButton, SellInvoiceRecalculateButton } from '../../../SellInvoiceButtons'
import { sellInvoiceStatusInversed } from '@teinor/erp/types/company/order/sell/sellInvoice'
import { ButtonPrimary } from '../../../../../../../../../../../baseComponents/Button'
import ModalFinalizeSellInvoice from '../ModalFinalizeSellInvoice'
import TwinTrans from '../../../../../../../../../../../baseComponents/TwinTrans'
import ModalDeleteSellInvoiceDraft from './ModalDeleteSellInvoiceDraft'
import { ButtonOrderInfoLeftBase, OrderDivideIntoChaptersButton, OrderProductTypesButton, OrderVariablesButton } from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderButtons'
import ModalSkipOperateStockOrder from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderTable/TableComponents/ModalsOrderTable/ModalSkipOperateStockOrder'
import { OrderStepButtonsBase, OrderStepButtonsBaseComponentProps } from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderButtonsBaseComponent/types'
import { OrderStepButtonsDefaultBaseComponent, OrderStepButtonsNewStyleBaseComponent } from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderButtonsBaseComponent'
import ModalOrderPaymentMethod from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderTable/TableComponents/ModalsOrderTable/ModalOrderPaymentMethod'

const SellInvoiceCreateStepButtons: React.FC<OrderStepButtonsBaseComponentProps> = ({ data, onSubmit, urls, setCompleteData,  ...rest }) => {
    const { infoMessageSellInvoice } = useSellInvoiceCreateStepButtonsLogic({ data, onSubmit, urls, ...rest })
    const OrderId = data?.header?.id
    return (
        <div className='flex items-center relative gap-5 flex-wrap'>
            <OrderStepButtonsDefaultBaseComponent {...{ data, onSubmit, hookFunction: useSellInvoiceCreateStepButtonsLogic, ...rest }} className='mr-8' />
            <SellInvoiceProcessButton data={data} onSubmit={onSubmit} url={urls?.sellInvoice} infoMessage={infoMessageSellInvoice || ''} {...{ OrderId, setCompleteData, sellStatus: sellInvoiceStatusInversed['accepted'] }}/>
        </div>
    )
}

const useSellInvoiceCreateStepButtonsLogic = ({ data, onSubmit, userPermissions, orderHeaderURLs, setCompleteData, updateHeaderData }: OrderStepButtonsBaseComponentProps) => {
    const { t } = useTwinTranslation()
    const name = 'sell_invoice_create_step_buttons'
    let infoMessageSellInvoice = checkSellFromOrderAllOk(data, 'sellInvoice', t)
    const Order = data?.header
    const hideQuickAccessButton = !Order?.QuoteOrderId && !Order?.SellOrderId && !Order?.SellDeliveryNoteId && !Order?.SellInvoiceId && !Order?.WorkSheetId && !Order?.relatedSellOrders?.length && !Order?.relatedSellDeliveryNotes?.length && !Order?.relatedSellInvoices?.length && !Order?.relatedWorkSheets?.length

    const defaultButtons = [{ "id": "previewSellInvoice" }, { "id": "downloadSellInvoice" }, { "id": "quickAccess" }, { "id": "duplicateSell" }]

    const sellInvoiceDuplicateText = t('duplicateInvoice', 'Duplicar factura')
    const sellInvoiceRecalculateText = t('recalculateSellInvoice', 'Recalcular factura')
    const sellInvoiceDownloadTagsText = t('printTags', 'Imprimir etiquetas')
    const sellInvoiceDeleteText = t('deleteDraft', 'Eliminar borrador')
    const sellInvoiceDownloadCSVText = t('downloadCsv', 'Descargar csv')
    const sellInvoiceQuickAccessText = t('quickAccess', 'Acceso rápido')
    const sellInvoicePDFPreviewText = t('previewPdf', 'Previsualizar PDF')
    const sellInvoiceDownloadPDFTemplateText = t('downloadPdfTemplate', 'Descargar plantilla PDF')
    const sellInvoiceDownloadPDFText = t('downloadPdf', 'Descargar PDF')
    const sellInvoiceViewWorkSheetText = t('goToWorkSheet', 'Ir a la hoja de trabajo')
    const sellInvoiceDivideIntoChaptersText = t('divideIntoChapters', 'Dividir en capítulos')
    const sellInvoiceVariablesText = t('variables', 'Variables')
    const sellInvoiceProductTypesText = t('valueProductTypes', 'Valor tipos de producto')

    const ButtonComponents: OrderStepButtonsBase = useMemo(() => {
        return {
            duplicateSell: {
                component: SellInvoiceDuplicateButton,
                extraProps: {
                    title: sellInvoiceDuplicateText,
                    id: 'duplicateSell',
                    allRowData: data,
                }
            },
            recalculateSellInvoice: {
                component: SellInvoiceRecalculateButton,
                extraProps: {
                    title: sellInvoiceRecalculateText,
                    id: 'recalculateSellInvoice',
                    allRowData: data,
                    onSubmit
                }
            },
            downloadSellTag: {
                component: SellDownloadTags,
                extraProps: {
                    title: sellInvoiceDownloadTagsText,
                    id: 'downloadSellTag',
                    Order,
                    typeTemplate:'sellInvoice'
                }
            },
            deleteSellInvoice: {
                component: SellInvoiceDeleteButton,
                showButton: !Order?.SellOrderId && !Order?.SellDeliveryNoteId,
                extraProps: {
                    title: sellInvoiceDeleteText,
                    id: 'deleteSellInvoice',
                    allRowData: data,
                }
            },
            downloadSellCsv: {
                component: SellDownloadButtonCSV,
                extraProps: {
                    title: sellInvoiceDownloadCSVText,
                    id: 'downloadSellCsv',
                    Order,
                    typeTemplate: 'sellInvoice'
                }
            },
            quickAccess: {
                component: SellQuickAccessButton,
                showButton: !hideQuickAccessButton,
                extraProps: {
                    title: sellInvoiceQuickAccessText,
                    id: 'quickAccess',
                    Order,
                }
            },
            previewSellInvoice: {
                component: SellPdfPreview,
                extraProps: {
                    title: sellInvoicePDFPreviewText,
                    id: 'previewSellInvoice',
                    Order,
                    typeTemplate:'sellInvoice'
                }
            },
            downloadSellInvoiceTemplate: {
                component: SellDownloadPdfTemplateButton,
                extraProps: {
                    title: sellInvoiceDownloadPDFTemplateText,
                    id: 'downloadSellInvoiceTemplate',
                    Order,
                    typeTemplate: 'sellInvoice',
                    key:'downloadSellInvoicePDFTemplate'
                }
            },
            downloadSellInvoice: {
                component: SellDownloadButtonPDF,
                extraProps: {
                    title: sellInvoiceDownloadPDFText,
                    id: 'downloadSellInvoice',
                    Order,
                    typeTemplate: 'sellInvoice',
                }
            },
            viewWorkSheet: {
                component: SellViewWorkSheetButton,
                showButton: Order?.WorkSheetId,
                extraProps: {
                    title: sellInvoiceViewWorkSheetText,
                    id: 'viewWorkSheet',
                    WorkSheetId: Order?.WorkSheetId
                }
            },
            divideIntoChaptersQuote: {
                component: OrderDivideIntoChaptersButton,
                extraProps: {
                    title: sellInvoiceDivideIntoChaptersText,
                    id: 'divideIntoChaptersQuote',
                    allRowData: data,
                    userPermissions,
                    onSubmit,
                    orderHeaderURLs,
                    setCompleteData,
                    updateHeaderData
                }
            },
            variablesQuote: {
                component: OrderVariablesButton,
                extraProps: {
                    title: sellInvoiceVariablesText,
                    id: 'variablesQuote',
                    allRowData: data,
                    userPermissions,
                    onSubmit,
                    orderHeaderURLs,
                    setCompleteData,
                    updateHeaderData
                }
            },
            sellProductTypes: {
                component: OrderProductTypesButton,
                extraProps: {
                    title: sellInvoiceProductTypesText,
                    id: 'sellProductTypes',
                    allRowData: data,
                    userPermissions,
                    onSubmit,
                    orderHeaderURLs,
                    setCompleteData,
                    updateHeaderData,
                }
            },
        }
    }, [Order, data, hideQuickAccessButton, onSubmit, orderHeaderURLs, sellInvoiceDeleteText, sellInvoiceDivideIntoChaptersText, sellInvoiceDownloadCSVText, sellInvoiceDownloadPDFTemplateText, sellInvoiceDownloadPDFText, sellInvoiceDownloadTagsText, sellInvoiceDuplicateText, sellInvoicePDFPreviewText, sellInvoiceQuickAccessText, sellInvoiceRecalculateText, sellInvoiceVariablesText, sellInvoiceViewWorkSheetText, setCompleteData, updateHeaderData, userPermissions, sellInvoiceProductTypesText])

    return { infoMessageSellInvoice, name, ButtonComponents, defaultButtons }
}

export const SellInvoiceCreateStepButtonsNew: React.FC<OrderStepButtonsBaseComponentProps> = ({ data, onSubmit, urls, setCompleteData, ...rest }) => {
    const { infoMessageSellInvoice } = useSellInvoiceCreateStepButtonsLogic({ data, onSubmit, urls, ...rest })
    const OrderId = data?.header?.id
    return (
        <div className='flex order_config_buttons_parent items-center relative gap-5 flex-wrap'>
            <OrderStepButtonsNewStyleBaseComponent {...{ data, onSubmit, hookFunction: useSellInvoiceCreateStepButtonsLogic, ...rest }} className='mr-8' />
            <SellInvoiceProcessButton data={data} onSubmit={onSubmit} url={urls?.sellInvoice} infoMessage={infoMessageSellInvoice || ''} {...{ OrderId, setCompleteData, sellStatus: sellInvoiceStatusInversed['accepted'] }} />
        </div>
    )
}

interface SellInvoiceDeleteButtonProps {
    allRowData: OrderData
    title: string
    id: string
    children?: React.ReactNode
}

const SellInvoiceDeleteButton: React.FC<SellInvoiceDeleteButtonProps> = ({ allRowData, id, title, children }) => {
    const { openModal, setOpenModal, myOnSubmit } = useSellInvoiceDeleteButtonLogic()
    const userInfo = getUserInfo()
    const EmployeeId = userInfo?.Employee?.id
    const orderEmployeeId = allRowData?.header?.CreateEmployeeId
    const isMyQuote = orderEmployeeId === EmployeeId
    if (!isMyQuote) {
        return null
    }
    return (
        <PermissionChecker permission='delete' userPermissions={getUserPermissions('sell.invoice')}>
            <div onClick={() => setOpenModal({ type: 'delete', allRowData })}>
                {children ?? <TwinIconToolTip classNameIcon='hover:text-red-BA regular22' message={title} icon={faTrashCan} id={id} />}
            </div>
            {openModal?.type === 'delete' ? <ModalDeleteSellInvoiceDraft setOpened={() => setOpenModal(null)} allRowData={allRowData} onSubmit={myOnSubmit} /> : null}
        </PermissionChecker>
    )
}

const useSellInvoiceDeleteButtonLogic = () => {
    const { openModal, setOpenModal } = useEditDeleteModal()
    const navigate = useNavigate()
    const location = useLocation()
    const basePath = getBasePath(location.pathname, 'sellInvoices')

    const myOnSubmit = useCallback(() => {
        navigate(basePath)
    }, [navigate, basePath])
    return { openModal, setOpenModal, myOnSubmit }
}

interface SellInvoiceButtonProps {
    url: string
    data: OrderData
    OrderId: number
    infoMessage: string
    setCompleteData?: SetCompleteDataTy
    className?: string
    onSubmit?: () => void
}

export const SellInvoiceProcessButton: React.FC<SellInvoiceButtonProps> = ({ infoMessage, url, OrderId, setCompleteData,  data, onSubmit }) => {
    const { openModal, setOpenModal } = useEditDeleteModal()
    return (
        <ButtonOrderInfoLeftBase disabled={infoMessage ? true : false} infoMessage={infoMessage} >
            <ButtonPrimary onClick={() => setOpenModal({ allRowData: { id: OrderId, status: sellInvoiceStatusInversed['accepted'] }, type: 'create' })} disabled={infoMessage ? true : false} >
                <TwinTrans transKey='processInvoice'>Procesar factura</TwinTrans>
            </ButtonPrimary>
            {openModal?.type === 'create' ? <ModalFinalizeSellInvoice data={data} url={url} setOpened={() => {}} setOpenModal={setOpenModal} onSubmit={() => onSubmit?.()} allRowData={openModal.allRowData} /> : null}
            {openModal?.type === 'edit' ? <ModalSkipOperateStockOrder checkStockURLs={'/api/app/order/sell/sellInvoice/checkAllSellInvoiceProductsStock'} setOpenModal={setOpenModal} allRowData={openModal.allRowData} finalizeOrder={openModal.allRowData?.finalizeOrder} /> : null}
            {openModal?.type === 'delete' ? <ModalOrderPaymentMethod headerData={data.header} setOpened={() => setOpenModal(null)} allRowData={{ ...openModal.allRowData, url: '/api/app/order/sell/sellInvoice/assignPaymentMethodToSellInvoice' }} totalsOrderTableData={openModal.allRowData?.totalsOrderTableData} beforeSubmit={openModal.allRowData.finalizeOrder} setCompleteData={setCompleteData} onSubmit={openModal.allRowData.onSubmit}/> : null}
        </ButtonOrderInfoLeftBase>
    )
}

export default SellInvoiceCreateStepButtons